import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import AnimatedHero from '../components/AnimatedHero'
import EpisodeCard from '../components/EpisodeCard'
import BadgeRow from '../components/BadgeRow'
import Form from '../components/Form'
import SEO from '../utils/seo'

const API_URL = import.meta.env.VITE_API_URL || '/api'

const Home = () => {
  const [latestEpisodes, setLatestEpisodes] = useState([])

  useEffect(() => {
    const fetchEpisodes = async () => {
      try {
        const response = await fetch(`${API_URL}/episodes`)
        const data = await response.json()
        setLatestEpisodes(data.slice(0, 3))
      } catch (error) {
        console.error('Error fetching episodes:', error)
      }
    }
    fetchEpisodes()
  }, [])

  return (
    <>
      <SEO />

      {/* Hero Section */}
      <AnimatedHero />

      {/* How It Works Section */}
      <section className="section bg-slate/20">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-off-white mb-4">
              One Prompt, Many Thoughts
            </h2>
            <p className="text-lg text-gray-400 max-w-2xl mx-auto mb-8">
              Every episode starts with a single, focused question. We go deep—exploring design, architecture, and the practical realities of shipping AI-native software.
            </p>

            {/* System Context Highlight */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="max-w-3xl mx-auto mb-16"
            >
              <div className="p-6 bg-gradient-to-br from-purple/10 via-slate/30 to-cyan/10 rounded-xl border-2 border-purple/30">
                <div className="text-xs font-mono text-purple mb-3 uppercase tracking-widest font-bold">
                  Our Format
                </div>
                <blockquote className="text-xl md:text-2xl font-semibold text-off-white leading-relaxed italic">
                  <span className="text-purple/60 text-3xl">"</span>
                  From prompt to production—real tech, real talk.
                  <span className="text-purple/60 text-3xl">"</span>
                </blockquote>
              </div>
            </motion.div>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                title: 'The Prompt',
                description:
                  'A single focused question about Gen AI, app development, DX, or data—the foundation of every episode.',
                icon: '💭',
              },
              {
                title: 'Deep Exploration',
                description:
                  'We unpack architecture, design patterns, and real-world trade-offs with engineers who ship production systems.',
                icon: '🔍',
              },
              {
                title: 'Practical Code',
                description:
                  'Patterns, pitfalls, and solutions you can apply to your own projects immediately.',
                icon: '⚡',
              },
            ].map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                className="bg-slate/40 backdrop-blur-sm rounded-lg p-8 border border-slate hover:border-cyan/50 transition-all duration-300"
              >
                <div className="text-5xl mb-4">{item.icon}</div>
                <h3 className="text-xl font-bold text-off-white mb-3">{item.title}</h3>
                <p className="text-gray-400">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Latest Episodes */}
      {latestEpisodes.length > 0 && (
        <section className="section">
          <div className="container-custom">
            <div className="flex justify-between items-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-off-white">Latest Episodes</h2>
              <Link
                to="/episodes"
                className="text-cyan hover:text-cyan/80 font-semibold gradient-underline"
              >
                View All →
              </Link>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {latestEpisodes.map((episode, index) => (
                <EpisodeCard key={episode.slug} episode={episode} index={index} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Subscribe Section */}
      <section className="section bg-slate/20">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-off-white mb-4">
              Watch & Listen Everywhere <span className="text-xl text-purple font-mono">(Coming Soon)</span>
            </h2>
            <p className="text-lg text-gray-400 mb-12 max-w-2xl mx-auto">
              Full episodes on YouTube, plus audio on all major podcast platforms
            </p>
            <BadgeRow />
          </motion.div>
        </div>
      </section>

      {/* Newsletter Section */}
      <section className="section">
        <div className="container-custom">
          <div className="max-w-xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-center mb-8"
            >
              <h2 className="text-3xl font-bold text-off-white mb-4">Stay Updated</h2>
              <p className="text-gray-400">
                Get notified when new episodes drop and receive exclusive content.
              </p>
            </motion.div>
            <Form type="newsletter" />
          </div>
        </div>
      </section>
    </>
  )
}

export default Home
